/**
 * OutboxToastObserver — global listener that surfaces toast
 * notifications when the SW outbox flushes a queued mutation
 * ("✓ Sent: warranty claim — was queued offline") or gives up
 * after MAX_ATTEMPTS retries.
 *
 * Wired once at the App level so any page's enqueue() flow gets a
 * consistent UX without each page subscribing individually.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import useOfflineQueue from "@/lib/useOfflineQueue";

export default function OutboxToastObserver() {
  const t = useT();
  const { drained, failed } = useOfflineQueue();
  const lastDrained = useRef(0);
  const lastFailed = useRef(0);

  useEffect(() => {
    if (drained > lastDrained.current) {
      const delta = drained - lastDrained.current;
      lastDrained.current = drained;
      toast.success(
        delta === 1
          ? t("outbox.toast_sent_one", "Queued request sent ✓")
          : t("outbox.toast_sent_n", "{count} queued requests sent ✓").replace("{count}", String(delta)),
        { duration: 4000 }
      );
    }
  }, [drained, t]);

  useEffect(() => {
    if (failed > lastFailed.current) {
      const delta = failed - lastFailed.current;
      lastFailed.current = failed;
      toast.error(
        delta === 1
          ? t("outbox.toast_failed_one", "A queued request couldn't be delivered. Please retry manually.")
          : t("outbox.toast_failed_n", "{count} queued requests couldn't be delivered.").replace("{count}", String(delta)),
        { duration: 8000 }
      );
    }
  }, [failed, t]);

  return null;
}
